import { ReactNode, useEffect, useState } from 'react';
import {
  AppBar,
  alpha,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAtom } from 'jotai';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '@assets/logo.svg';
import { layout } from '@/theme';
import DashboardShellHeader from './Components/DashboardShellHeader';
import { getHeaderBackground, headerActionSx } from './headerStyles';
import dashboardSections, { DashboardNavItem } from './dashboardSections';
import { isDrawerExpandedAtom } from './hook';

interface IDashboardShellProps {
  children: ReactNode;
}

const isPathActive = (pathname: string, path: string) => {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
};

type NavListProps = {
  items: DashboardNavItem[];
  showLabels: boolean;
  pathname: string;
  onNavigate: () => void;
};

const NavList = ({ items, showLabels, pathname, onNavigate }: NavListProps) => (
  <List sx={{ px: 1.25, py: 1.25 }}>
    {items.map(({ id, name, path, Icon }) => {
      const active = isPathActive(pathname, path);
      const button = (
        <ListItemButton
          component={RouterLink}
          to={path}
          selected={active}
          aria-current={active ? 'page' : undefined}
          aria-label={showLabels ? undefined : name}
          onClick={onNavigate}
          sx={{
            minHeight: 44,
            borderRadius: 1.5,
            justifyContent: showLabels ? 'initial' : 'center',
            px: showLabels ? 1.5 : 1,
            color: 'text.primary',
            transition: (theme) =>
              theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&.Mui-selected': (theme) => ({
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.primary.light
                  : alpha(theme.palette.primary.main, 0.18),
              color:
                theme.palette.mode === 'light'
                  ? theme.palette.primary.dark
                  : theme.palette.info.main,
              boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'light'
                    ? theme.palette.primary.light
                    : alpha(theme.palette.primary.main, 0.24),
              },
            }),
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: showLabels ? 1.5 : 0,
              justifyContent: 'center',
              color: 'text.secondary',
              '& svg': {
                fontSize: 20,
              },
            }}
          >
            <Icon />
          </ListItemIcon>
          {showLabels && (
            <ListItemText
              primary={name}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            />
          )}
        </ListItemButton>
      );

      return (
        <ListItem key={id} disablePadding sx={{ display: 'block', mb: 0.25 }}>
          {showLabels ? (
            button
          ) : (
            <Tooltip title={name} placement="right">
              {button}
            </Tooltip>
          )}
        </ListItem>
      );
    })}
  </List>
);

const DashboardShell = ({ children }: IDashboardShellProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useAtom(isDrawerExpandedAtom);
  const [mobileOpen, setMobileOpen] = useState(false);

  const showLabels = isMobile || isExpanded;
  const sidebarWidth = showLabels
    ? layout.sidebarWidth
    : layout.sidebarCollapsedWidth;

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const toggleNavigation = () => {
    if (isMobile) {
      setMobileOpen((open) => !open);
      return;
    }
    setIsExpanded((prev) => !prev);
  };

  const closeMobileNavigation = () => {
    if (isMobile) setMobileOpen(false);
  };

  let navigationLabel = 'Expand navigation';
  if (isMobile) {
    navigationLabel = mobileOpen ? 'Close navigation' : 'Open navigation';
  } else if (isExpanded) {
    navigationLabel = 'Collapse navigation';
  }

  const primaryItems = dashboardSections.filter(
    (item) => item.placement === 'primary',
  );
  const footerItems = dashboardSections.filter(
    (item) => item.placement === 'footer',
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <AppBar
        position="fixed"
        elevation={0}
        color="primary"
        enableColorOnDark
        sx={(muiTheme) => {
          const isDark = muiTheme.palette.mode === 'dark';
          return {
            zIndex: muiTheme.zIndex.drawer + 1,
            bgcolor: getHeaderBackground(muiTheme),
            color: muiTheme.palette.common.white,
            borderBottom: `1px solid ${alpha(
              muiTheme.palette.common.white,
              isDark ? 0.08 : 0.12,
            )}`,
            boxShadow: `0 1px 8px ${alpha(
              isDark
                ? muiTheme.palette.common.black
                : muiTheme.palette.primary.dark,
              isDark ? 0.4 : 0.18,
            )}`,
          };
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            height: layout.toolbarHeight,
            gap: 2,
            justifyContent: 'space-between',
            // On desktop the menu button is centred over the collapsed
            // navigation rail; the right edge lines up with the page gutter.
            pl: { xs: 2, md: 2.25 },
            pr: { xs: 2, sm: 3.5 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <IconButton
              aria-label={navigationLabel}
              aria-expanded={isMobile ? mobileOpen : isExpanded}
              aria-controls="primary-navigation"
              onClick={toggleNavigation}
              sx={headerActionSx}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                minWidth: 0,
              }}
            >
              {/* logo.svg pads its square icon with transparent space on both
                  sides; object-fit crops it back to the icon itself. */}
              <Box
                component="img"
                src={logo}
                alt=""
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: 'cover',
                  display: 'block',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="subtitle1"
                component="div"
                noWrap
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  lineHeight: 1,
                  color: 'inherit',
                }}
              >
                SYNAPSYS
              </Typography>
            </Box>
          </Box>
          <DashboardShellHeader />
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: false }}
        sx={{
          ...(isMobile
            ? { zIndex: theme.zIndex.drawer }
            : { width: sidebarWidth, flexShrink: 0 }),
          whiteSpace: 'nowrap',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: isMobile ? layout.sidebarWidth : sidebarWidth,
            boxSizing: 'border-box',
            height: '100%',
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            overflowX: 'hidden',
            boxShadow: isMobile
              ? undefined
              : `8px 0 20px ${alpha(
                  theme.palette.common.black,
                  theme.palette.mode === 'dark' ? 0.2 : 0.025,
                )}`,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        <Toolbar />
        <Box
          component="nav"
          id="primary-navigation"
          aria-label="Primary"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <NavList
            items={primaryItems}
            showLabels={showLabels}
            pathname={pathname}
            onNavigate={closeMobileNavigation}
          />
          <Box>
            <Divider />
            <NavList
              items={footerItems}
              showLabels={showLabels}
              pathname={pathname}
              onNavigate={closeMobileNavigation}
            />
          </Box>
        </Box>
      </Drawer>

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Toolbar />
        <Box
          component="main"
          sx={{
            '--page-gutter': (muiTheme) => muiTheme.spacing(2),
            [theme.breakpoints.up('sm')]: {
              '--page-gutter': (muiTheme) => muiTheme.spacing(3.5),
            },
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            bgcolor: 'background.dashboardMainContent',
            px: 'var(--page-gutter)',
            pt: 'var(--page-gutter)',
            pb: 'var(--page-gutter)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardShell;
